// src/components/onboarding/TranslationStandbyScreen.tsx
//
// Shown while the Cloud Function is generating translations for
// the user's chosen language. Same brand palette and typography
// hooks as LanguageGateScreen so it feels like phase 3 of the
// gate flow rather than a different screen.
//
// What the user sees:
//   - Logo
//   - Native-script name of the language being prepared
//   - Animated linear progress bar driven by the
//     useTranslationStatus hook (completed / total)
//   - "Preparing your language" caption
//   - Tiny, dismissable "Continue in English" link as a fallback
//     for users who don't want to wait — selecting it clears the
//     pending choice and lets them through with the English
//     dictionary. Optional, controlled by the `onSkip` prop.

import React from 'react';
import { Box, Stack, Typography, LinearProgress, ButtonBase } from '@mui/material';
import { Logo } from '../Logo';
import { LanguageEntry } from '../../i18n/languages';
import { useT } from '../../i18n/useT';

interface TranslationStandbyScreenProps {
  language: LanguageEntry;
  /** Number of strings already translated. */
  completed: number;
  /** Total strings to translate. May be 0 briefly before the
   *  first doc-snap arrives — we render an indeterminate bar in
   *  that case. */
  total: number;
  /** Optional escape hatch. When provided, renders a small link
   *  the user can tap to dismiss the standby screen and continue
   *  with English (the dictionary's safety-net fallback). */
  onSkip?: () => void;
}

export const TranslationStandbyScreen: React.FC<TranslationStandbyScreenProps> = ({
  language,
  completed,
  total,
  onSkip,
}) => {
  const t = useT();
  const indeterminate = total <= 0;
  const pct = indeterminate ? 0 : Math.min(100, Math.round((completed / total) * 100));

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: '#FAFAF7',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Match the gate screen's atmosphere — same gradient mesh
        // so the transition between phases feels seamless.
        backgroundImage:
          'radial-gradient(ellipse 60% 40% at 20% 15%, rgba(15,76,117,0.06), transparent 70%), radial-gradient(ellipse 50% 35% at 85% 90%, rgba(224,122,95,0.07), transparent 70%)',
        animation: 'standbyFadeIn 360ms ease-out both',
        '@keyframes standbyFadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      <Stack
        alignItems="center"
        spacing={4}
        sx={{ px: 3, maxWidth: 420, width: '100%' }}
      >
        <Logo size={32} alt={t("Instant Tours")} />

        <Typography
          dir={language.rtl ? 'rtl' : 'ltr'}
          sx={{
            fontFamily: '"Fraunces", "Iowan Old Style", "Palatino", Georgia, serif',
            fontWeight: 500,
            fontSize: { xs: 36, sm: 48 },
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: '#1A1A1A',
            textAlign: 'center',
          }}
        >
          {language.welcome}
        </Typography>

        <Stack alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
          <Typography
            sx={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(26, 26, 26, 0.55)',
              fontWeight: 500,
            }}
          >
            
            {t("preparing")} {language.englishName.toLowerCase()}
          </Typography>

          <Box sx={{ width: '100%', mt: 1 }}>
            <LinearProgress
              variant={indeterminate ? 'indeterminate' : 'determinate'}
              value={pct}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(15,76,117,0.08)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#0F4C75',
                  borderRadius: 2,
                },
              }}
            />
          </Box>

          {!indeterminate && (
            <Typography
              sx={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 12,
                color: 'rgba(26,26,26,0.45)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {completed} / {total}
            </Typography>
          )}
        </Stack>

        <Typography
          sx={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 13,
            color: 'rgba(26,26,26,0.55)',
            textAlign: 'center',
            maxWidth: 320,
            mt: 1,
          }}
        >
          
          {t("We're translating the app for you. This happens once — next time you'll come straight in.")}
        </Typography>

        {onSkip && (
          <ButtonBase
            onClick={onSkip}
            sx={{
              mt: 1,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13,
              color: 'rgba(15, 76, 117, 0.8)',
              textDecoration: 'underline',
              textUnderlineOffset: 4,
              p: 1,
              borderRadius: 1,
              '&:hover, &:focus-visible': {
                color: '#0F4C75',
                bgcolor: 'rgba(15,76,117,0.05)',
              },
            }}
          >
            
            {t("Continue in English for now")}
          </ButtonBase>
        )}
      </Stack>
    </Box>
  );
};
